/**
 * AMLProfileSignalsPanel — read-only display of the five AML completion
 * signals (Brief 86 / 14f.A). Phase 1 is capture-only, so this panel never
 * mutates state; it surfaces what MLCO/COLP capture elsewhere.
 *
 *   S1  Thirdfort report received
 *   S2  Source of funds verified
 *   S3  Source of wealth verified
 *   S4  PEP / sanctions screening clear
 *   S5  Ongoing monitoring schedule set
 *
 * Accepts an optional `profile` prop (used by tests and any parent that has
 * already fetched). When only `matterReference` is given it self-fetches via
 * GET /api/aml/{ref}/profile. Tolerates missing/partial signal data.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../../config/api";

import { errorMessage } from "../../utils/errorMessage";
const SIGNALS = [
  { key: "thirdfort_report_received", code: "S1", label: "Thirdfort report received" },
  { key: "source_of_funds_verified", code: "S2", label: "Source of funds verified" },
  { key: "source_of_wealth_verified", code: "S3", label: "Source of wealth verified" },
  { key: "pep_sanctions_clear", code: "S4", label: "PEP / sanctions screening clear" },
  { key: "ongoing_monitoring_set", code: "S5", label: "Ongoing monitoring schedule set" },
];

function authHeaders() {
  const email =
    sessionStorage.getItem("bonsai_user_id") ||
    localStorage.getItem("bonsai_user_id") ||
    "";
  return email
    ? { "X-Bonsai-User-Id": email }
    : {};
}

function formatWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

export default function AMLProfileSignalsPanel({ profile: profileProp, matterReference }) {
  const [profile, setProfile] = useState(profileProp || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (profileProp) {
      setProfile(profileProp);
      return;
    }
    if (!matterReference) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios
      .get(`${API}/aml/${encodeURIComponent(matterReference)}/profile`, {
        headers: authHeaders(),
      })
      .then((res) => {
        if (cancelled) return;
        setProfile(res?.data?.aml_profile || null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          errorMessage(err, "Could not load AML profile.")
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [profileProp, matterReference]);

  const signals = (profile && profile.completion_signals) || {};

  return (
    <div
      className="space-y-2 rounded-md border border-slate-700/60 bg-slate-900/40 p-3"
      data-testid="aml-profile-signals-panel"
    >
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-300">
          AML source-data signals
        </h4>
        {loading && (
          <span className="text-xs text-slate-500" data-testid="aml-signals-loading">
            Loading…
          </span>
        )}
      </div>

      {error && (
        <div className="text-xs text-amber-400" data-testid="aml-signals-error">
          {error}
        </div>
      )}

      <ul className="space-y-1.5" data-testid="aml-signals-list">
        {SIGNALS.map(({ key, code, label }) => {
          const sig = signals[key] || {};
          const captured = !!sig.captured;
          return (
            <li
              key={key}
              className="flex items-start justify-between gap-2 text-sm"
              data-testid={`aml-signal-${key}`}
            >
              <span className="flex items-start gap-2">
                <span
                  className={
                    captured
                      ? "mt-0.5 inline-block h-2 w-2 rounded-full bg-emerald-500"
                      : "mt-0.5 inline-block h-2 w-2 rounded-full bg-slate-600"
                  }
                  data-testid={`aml-signal-${key}-dot`}
                  aria-hidden="true"
                />
                <span>
                  <span className="font-medium text-slate-200">
                    <span className="text-slate-500">{code}</span> {label}
                  </span>
                  {captured && (sig.captured_by || sig.captured_at) && (
                    <span
                      className="block text-xs text-slate-400 mt-0.5"
                      data-testid={`aml-signal-${key}-meta`}
                    >
                      {sig.captured_by ? sig.captured_by : "captured"}
                      {sig.captured_at ? ` · ${formatWhen(sig.captured_at)}` : ""}
                    </span>
                  )}
                  {sig.note && (
                    <span className="block text-xs text-slate-500 mt-0.5">
                      {sig.note}
                    </span>
                  )}
                </span>
              </span>
              <span
                className={
                  captured
                    ? "shrink-0 text-xs font-medium text-emerald-400"
                    : "shrink-0 text-xs text-slate-500"
                }
                data-testid={`aml-signal-${key}-status`}
              >
                {captured ? "Captured" : "Not captured"}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
